import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MetadataFormService } from '@app/core/services/metadata/metadata-form.service';
import { MetadataTableService } from '@app/core/services/metadata/metadata-table.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { CultureService } from '@app/core/services/system-language/culture.service';
import { Form, FormElement, Table } from '@app/modules/form-generator/edit-form-v3/models/form.model';
import { EmployeeSecurityService } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-security/services/employee-security.service';
import { SelectSearchState } from '@app/shared/components/select-search/select-search-state';
import { Localization } from '@app/shared/models/localization.model';
import { Culture } from '@app/shared/models/system-language/culture.model';
import { LocalizationFormService } from '@app/shared/services/localization-form.service';
import moment from 'moment';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-form-details-form',
  templateUrl: './form-details-form.component.html',
  styleUrls: ['./form-details-form.component.scss']
})
export class FormDetailsFormComponent implements OnInit {
  @Input() formId: string;
  @Output() saved = new EventEmitter<Localization[]>();
  form: UntypedFormGroup;
  cultures: Culture[];
  formDetails: Form;
  tables: Table[] = [];
  saving: boolean = false;

  // Describes the name form array to app-localization-text-form-field
  nameField = {
    formControl: 'name',
    localizationValueParam: 'text',
    text: 'Form name',
    disabled: 'false'
  } as FormElement;

  tableSearch = new SelectSearchState<Table>(
    () => this.tables,
    table => table.name,
    () => this.tables.find(table => table.id === this.form?.get('table').value)
  );

  constructor(
    private fb: UntypedFormBuilder,
    private cultureService: CultureService,
    private metadataFormService: MetadataFormService,
    private metadataTableService: MetadataTableService,
    private snackbarService: SnackbarService,
    private employeeSecurityService: EmployeeSecurityService,
    private localizationFormService: LocalizationFormService,
  ) { }

  ngOnInit(): void {
    this.getFormDetails();
    this.getTables();
    this.cultureService.getCultures().subscribe( res => { this.cultures = res } );
  }

  // The current culture is needed to put the user's language first in the name translations
  getFormDetails() {
    forkJoin([
      this.metadataFormService.getForm(this.formId),
      this.employeeSecurityService.currentCulture$(),
    ])
      .subscribe(
        ([res]) => {
          this.formDetails = res;
          this.createForm();
        }
      );
  }

  getTables() {
    this.metadataTableService.getTables(1000, 0)
      .subscribe(
        res => {
          this.tables = res.data;
          this.tableSearch.refresh();
        }
      );
  }

  createForm() {
    this.form = this.fb.group({
      id: [this.formDetails ? this.formDetails.id : null],
      asOf: [moment().format()],
      name: this.localizationFormService.buildLocalizationArray(this.formDetails?.name),
      table: [this.formDetails ? this.formDetails.table?.id : null],
      changeReason: [''],
      changeReasonComments: [''],
    });
  }

  get localizations() {
    return this.form.controls["name"] as UntypedFormArray;
  }

  addNewLocalization() {
    this.localizations.push(this.localizationFormService.createLocalizationGroup());
    this.form.markAsDirty();
  }

  deleteLocalization(index: number) {
    this.localizations.removeAt(index);
    this.form.markAsDirty();
  }

  addLocalizationFromComponent(formControl: string) {
    if (formControl === 'name') {
      this.addNewLocalization();
    }
  }

  deleteLocalizationFromComponent(event: { index: number; formControl: string }) {
    if (event.formControl === 'name') {
      this.deleteLocalization(event.index);
    }
  }

  save() {
    if (this.form.invalid || this.saving) {
      return;
    }

    this.saving = true;
    this.metadataFormService.updateForm(this.formId, this.form.value)
    .pipe(
      finalize(() => this.saving = false)
    )
    .subscribe(
      (res) => {
          this.form.markAsPristine();
          this.saved.emit(this.form.value.name);
          this.snackbarService.openSnackBar('Form updated successfully', 'clear', 'success');
      },
    )
  }

}
